import * as React from "react";

import { usePairingContext } from "../hooks/pairingStatus";

import ClubInClubWidget, { clubInClubWidgetRenders } from "./ClubInClubWidget";
import HomeCallout from "./HomeCallout";
import { useHomeClubInClub } from "./HomeClubInClubContext";

/**
 * The first thing on the home screen.
 *
 * Ask Volley, except for members the Club-in-Club widget has something to say to, who get
 * that instead. The trial is measuring whether members act on their club standing when it
 * leads the screen, and two callouts competing for that position would blunt both — so
 * this is a swap rather than an addition.
 *
 * On court the order reverses. A member connected to a trainer is there to train, and Ask
 * Volley is the thing that can start an assessment for them; their point balance can wait
 * until they are off court, where it is the next thing they would act on. The membership
 * card becomes the fallback, so the swap never trades a card for a gap — see below.
 *
 * Keyed on whether each will actually render, NOT on the feature flag. The flag is
 * granted to a cohort; the widget only appears for those in it who have a Club-in-Club
 * record or can create one. Swapping on the flag meant everyone else — including every
 * admin the flag was granted to for testing — lost Ask Volley and got an empty space.
 * Ask Volley can come up empty for three separate reasons of its own (no flag, wrong
 * sport, no eligible prompt or a soft-dismiss), which is why it is handed the card rather
 * than asked whether it will render: it is the only thing that knows.
 *
 * Nothing renders while the answer is still loading, rather than showing Ask Volley and
 * swapping it out a moment later. That is a brief gap for the cohort only: everyone else
 * settles as soon as the feature list resolves, which is what Ask Volley waits for anyway.
 */
export default function HomeTopSlot(): React.ReactNode {
    const clubInClub = useHomeClubInClub();
    const { status } = usePairingContext();
    if (clubInClub.loading) return null;

    const card = clubInClubWidgetRenders(clubInClub) ? (
        <ClubInClubWidget />
    ) : null;

    // Null for anyone with no club standing, which leaves Ask Volley exactly as it was
    // for the large majority of users, on court and off.
    if (status === "paired") return <HomeCallout fallback={card} />;
    return card ?? <HomeCallout />;
}
